import { type ProgressionStatus, type ReflectionSummary } from "../backend/api";
import { type ReflectionState } from "../hooks/useReflection";
import { type MyData } from "../query/functions/user/data";
import { Routes } from "../router";

export class ReflectionRouter {
	constructor(private readonly reflectionService: ReflectionService) {}

	question(currentId?: string) {
		const reflection = this.reflectionService.getNextReflection(currentId);
		if (!reflection) {
			return Routes.Dashboard();
		}

		return Routes.ReflectionQuestion({
			id: reflection.id,
		});
	}

	nextQuestion(currentId?: string) {
		const reflection = this.reflectionService.getNextReflection(currentId);

		if (!reflection) {
			return Routes.Dashboard();
		}

		const unfinishedRequired = this.reflectionService.getReflections({
			optional: false,
			finished: false,
		});
		const allOptionalReflections = this.reflectionService.getReflections({ optional: true });
		const allOptionalsNotStarted = allOptionalReflections.every((r) => !r.reflectionChat?.status);

		if (!unfinishedRequired.length && allOptionalsNotStarted) {
			return Routes.ReflectionGenerate();
		}

		return Routes.ReflectionQuestion({
			id: reflection.id,
		});
	}

	dashboard(progression: ProgressionStatus, myData: MyData) {
		if (!myData.modal.reflectionExplainerGeneral.seen) {
			return Routes.ReflectionExplainer();
		}

		if (progression.reflection === "NOT_STARTED") {
			return this.question();
		}

		return Routes.Dashboard();
	}
}

export class ReflectionService {
	router: ReflectionRouter;

	constructor(private readonly reflectionState: ReflectionState) {
		this.router = new ReflectionRouter(this);
	}

	getReflections({
		optional = "both",
		finished = "both",
	}: {
		optional?: true | false | "both";
		finished?: true | false | "both";
	}) {
		let filteredReflections = this.reflectionState;

		if (optional !== "both") {
			filteredReflections = filteredReflections.filter((r) => r.optional === optional);
		}

		if (finished === true) {
			filteredReflections = filteredReflections.filter(
				(r) => r.reflectionChat?.status === "FINISHED",
			);
		} else if (finished === false) {
			filteredReflections = filteredReflections.filter(
				(r) => r.reflectionChat?.status !== "FINISHED",
			);
		}

		return filteredReflections;
	}

	splitOptional(reflections: ReflectionSummary[] = this.reflectionState) {
		return {
			optional: reflections.filter((r) => r.optional),
			required: reflections.filter((r) => !r.optional),
		};
	}

	splitFinished(reflections: ReflectionSummary[] = this.reflectionState) {
		return {
			finished: reflections.filter((r) => r.reflectionChat?.status === "FINISHED"),
			unfinished: reflections.filter((r) => r.reflectionChat?.status !== "FINISHED"),
		};
	}

	getProgress() {
		const { optional, required } = this.splitOptional();

		const { finished: requiredFinished } = this.splitFinished(required);
		if (requiredFinished.length !== required.length) {
			return {
				current: requiredFinished.length,
				total: required.length,
			};
		}

		const { finished: optionalFinished } = this.splitFinished(optional);
		return {
			current: requiredFinished.length + optionalFinished.length,
			total: required.length + optional.length,
		};
	}

	getNextReflection(currentId?: string) {
		const ordered = this.orderForUser(this.reflectionState);
		if (ordered.length === 0) {
			return null;
		}

		const idx = ordered.findIndex((r) => r.id === currentId);

		if (currentId === undefined || idx === -1) {
			return ordered[0];
		}

		return idx + 1 < ordered.length ? ordered[idx + 1] : null;
	}

	private orderForUser(reflections: ReflectionSummary[]) {
		const rank = (r: ReflectionSummary): number => {
			if (!r.optional) return 0;
			if (r.reflectionChat?.status === "SKIPPED") return 2;
			return 1;
		};

		return reflections
			.filter((r) => r.reflectionChat?.status !== "FINISHED")
			.toSorted((a, b) => rank(a) - rank(b));
	}
}
